import { useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl, type IntlShape, type MessageDescriptor } from 'react-intl'

import { useAppSelector } from 'hooks/reduxHooks'
import { slugify } from 'lib/string/stringUtils'
import {
  getOfferPageHighlightTravellerContext,
  getViewedOfferPageHighlightPropertyIds,
  setViewedOfferPageHighlightPropertyIds,
  shouldUseFirstNameForNeutralOfferPageHighlight,
  type OfferPageHighlightTravellerContext,
} from './offerPageHighlightTagsUtils'

export type OfferPageHighlightTagIcon =
  | 'bed-sofa'
  | 'car'
  | 'cloch'
  | 'clock'
  | 'dumbbell'
  | 'family'
  | 'glass-martini'
  | 'golf'
  | 'grapes'
  | 'kid'
  | 'map-marker'
  | 'parking'
  | 'plane'
  | 'restaurant'
  | 'shop'
  | 'ski-lift'
  | 'snow-flake'
  | 'star'
  | 'sunset'
  | 'swimmer'
  | 'theatre'
  | 'trees'
  | 'utensils'
  | 'walking'
  | 'water'
  | 'wellness'
  | 'wheelchair'
  | 'wifi'

export interface OfferPageHighlightTag {
  id: string
  icon: OfferPageHighlightTagIcon
  title: string
  description: string
}

export interface OfferPageHighlightTagContext {
  boardCodes?: Array<App.RateBoardCode>
  facilities?: Array<string>
  holidayTypes?: Array<string>
  inclusions?: Array<string>
  isAdultsOnly?: boolean
  locationTags?: Array<string>
  rating?: App.OfferRating
  roomAmenities?: Array<string>
  roomNames?: Array<string>
  roomSizeAvailableRoomCount?: number
  roomSizesSqm?: Array<number>
  roomViewTags?: Array<string>
}

interface Props {
  context?: OfferPageHighlightTagContext
  duration?: number
  hasSelectedDates: boolean
  isOccupancyContextKnown: boolean
  propertyId: string
  rooms?: Array<App.Occupants>
}

interface GetTitleOptions {
  duration?: number
  firstName?: string
  hasSelectedDates: boolean
  shouldUseFirstNameForNeutralContext: boolean
  travellerContext: OfferPageHighlightTravellerContext
}

interface OfferPageHighlightTagConfig {
  id: string
  icon: OfferPageHighlightTagIcon
  titleMessage: MessageDescriptor
  descriptionMessage: MessageDescriptor
}

interface OfferPageHighlightMappingConfig extends OfferPageHighlightTagConfig {
  isEligible: (options: OfferPageHighlightMappingOptions) => boolean
}

interface OfferPageHighlightMappingOptions {
  context?: OfferPageHighlightTagContext
  hasAllInclusive: boolean
  hasBreakfastIncluded: boolean
  hasChild: boolean
  hasFamilyFriendlySignals: boolean
  hasRomanticHolidayType: boolean
  hasTotalGuestCountForGroup: boolean
  isAdultsOnlyProperty: boolean
  isCouple: boolean
  isGroupOrFamily: boolean
  searchableText: Array<string>
}

const titleMessages = defineMessages({
  selectedDatesPersonalised: {
    id: '9qYr362JUbblxreWxURy',
    defaultMessage: '{firstName}, make the most of your {duration}-night escape',
    description: 'Personalised offer page highlight tags heading shown when a logged-in traveller has selected travel dates.',
  },
  selectedDates: {
    id: 'kw_ajbNItS2kRiUYRKj3',
    defaultMessage: 'Make the most of your {duration}-night escape',
    description: 'Offer page highlight tags heading shown when travel dates have been selected.',
  },
  familyPersonalised: {
    id: '1V7zZ4l7GenqmrZUT0qI',
    defaultMessage: '{firstName}, the kids are going to love this place',
    description: 'Personalised offer page highlight tags heading shown for family traveller context.',
  },
  family: {
    id: 'SqrTjifH_VLXlV1_rL4a',
    defaultMessage: 'Great for a family getaway',
    description: 'Offer page highlight tags heading shown for family traveller context without a logged-in traveller name.',
  },
  couplePersonalised: {
    id: 'rP_izX-p5-frQUfaIQgU',
    defaultMessage: '{firstName}, this is perfect for a romantic escape',
    description: 'Personalised offer page highlight tags heading shown for couple traveller context.',
  },
  couple: {
    id: 'KM5TYoW1-Po9X4j9e_UH',
    defaultMessage: 'Perfect for a romantic escape',
    description: 'Offer page highlight tags heading shown for couple traveller context without a logged-in traveller name.',
  },
  group: {
    id: 'BAoWe9-zW73zLiCxKjQ0',
    defaultMessage: 'Great for a getaway with friends',
    description: 'Offer page highlight tags heading shown for adult group traveller context without selected travel dates.',
  },
  neutralPersonalised: {
    id: 'fSY7ROMQAwflYfbpuY34',
    defaultMessage: '{firstName}, we think you’ll love this place',
    description: 'Personalised offer page highlight tags heading shown when no trip or occupancy context is available.',
  },
  neutral: {
    id: 'qFaSn8AG_efcM_5ohbt9',
    defaultMessage: 'Why travellers love this stay',
    description: 'Offer page highlight tags heading shown when no traveller context is available.',
  },
})

const tagMessages = defineMessages({
  familyPoolsTitle: {
    id: 'm6VxiTYi5HF5QkRZmQFh',
    defaultMessage: 'Family pools and activities',
    description: 'Title for a family-focused offer page highlight tag about pools and activities.',
  },
  familyPoolsDescription: {
    id: 'iDTBsx3S5rVNA2APUfPB',
    defaultMessage: 'Kids can splash and stay entertained',
    description: 'Description for a family-focused offer page highlight tag about kids activities.',
  },
  familyLocationTitle: {
    id: 'F3Hd4aE-tXT6-5Phb-Xq',
    defaultMessage: 'Close to beaches and fun',
    description: 'Title for a family-focused offer page highlight tag about nearby attractions.',
  },
  familyLocationDescription: {
    id: 'b2upeAOxCoCsBJXvX16z',
    defaultMessage: 'Easy family day trips nearby',
    description: 'Description for a family-focused offer page highlight tag about nearby attractions.',
  },
  familyDiningTitle: {
    id: '33pYXleyPg6Y_PNMf6WQ',
    defaultMessage: 'Dining for every taste',
    description: 'Title for a family-focused offer page highlight tag about varied dining options.',
  },
  familyDiningDescription: {
    id: '132vpLH-JhFAnxq2IKvQ',
    defaultMessage: 'Options for even picky eaters',
    description: 'Description for a family-focused offer page highlight tag about varied dining options.',
  },
  familySpaceTitle: {
    id: 'mzNjhW4GyJ8Mne7aNoCA',
    defaultMessage: 'Space for families to relax',
    description: 'Title for a family-focused offer page highlight tag about relaxing spaces.',
  },
  familySpaceDescription: {
    id: 'kApUhNaeVqHShqAKdUNy',
    defaultMessage: 'Pools, gardens and open areas',
    description: 'Description for a family-focused offer page highlight tag about relaxing spaces.',
  },
  coupleSunsetTitle: {
    id: 'LFtGgrCyH1NQ4lUx8wwB',
    defaultMessage: 'Sunset views together',
    description: 'Title for a couple-focused offer page highlight tag about watching sunsets together.',
  },
  coupleSunsetDescription: {
    id: 'cCZH6uD0zN0jLK-BTUgY',
    defaultMessage: 'Watch the sun dip below the horizon',
    description: 'Description for a couple-focused offer page highlight tag about watching sunsets together.',
  },
  coupleCocktailsTitle: {
    id: '2LoeAN8M7J7XHK_8i1We',
    defaultMessage: 'Cocktails and relaxed nights',
    description: 'Title for a couple-focused offer page highlight tag about evening drinks.',
  },
  coupleCocktailsDescription: {
    id: 'ibPP0OMZHcpjqkCLs6pX',
    defaultMessage: 'Enjoy drinks and laid-back evenings',
    description: 'Description for a couple-focused offer page highlight tag about evening drinks.',
  },
  coupleResortTitle: {
    id: '55lZReYMAmeWTDK6xeGU',
    defaultMessage: 'Peaceful resort atmosphere',
    description: 'Title for a couple-focused offer page highlight tag about a peaceful resort setting.',
  },
  coupleResortDescription: {
    id: 'vNQ8IX1XZHNRQLvBqYl2',
    defaultMessage: 'Quiet spaces to relax and reconnect',
    description: 'Description for a couple-focused offer page highlight tag about a peaceful resort setting.',
  },
  coupleServiceTitle: {
    id: 'GD71_Ga0zsS7Gtxq_3T7',
    defaultMessage: 'Highly rated for service',
    description: 'Title for a couple-focused offer page highlight tag about highly rated service.',
  },
  coupleServiceDescription: {
    id: '63eRbm0yguf-b7BypOaC',
    defaultMessage: 'Guests praise the warm hospitality',
    description: 'Description for a couple-focused offer page highlight tag about highly rated service.',
  },
  defaultRatingTitle: {
    id: 'Tw1v7IK5vqnbbQRdLlN9',
    defaultMessage: 'Highly rated by travellers',
    description: 'Title for a general offer page highlight tag about traveller ratings.',
  },
  defaultRatingDescription: {
    id: 'tBWq8agANFa171AFelj0',
    defaultMessage: 'Loved for its service and dining',
    description: 'Description for a general offer page highlight tag about traveller ratings.',
  },
  defaultLocationWithDatesTitle: {
    id: 'O4vIjpaVJxx_dNzpCU3w',
    defaultMessage: 'Prime location for exploring',
    description: 'Title for a general offer page highlight tag about location when travel dates are selected.',
  },
  defaultLocationTitle: {
    id: 'g7-YD45lDbU58JBAc_yV',
    defaultMessage: 'Great location to explore',
    description: 'Title for a general offer page highlight tag about location when no travel dates are selected.',
  },
  defaultLocationDescription: {
    id: 'nwFqhp648DX8wmcbqAJ_',
    defaultMessage: 'Close to beaches and restaurants',
    description: 'Description for a general offer page highlight tag about nearby beaches and restaurants.',
  },
  defaultPerksTitle: {
    id: 'zDYLmrrKehpGqGXzye6U',
    defaultMessage: 'Premium perks included',
    description: 'Title for a general offer page highlight tag about included premium perks.',
  },
  defaultPerksDescription: {
    id: 'mG7WQtaDJe3abxEMvvgf',
    defaultMessage: 'Enjoy curated extras and drinks',
    description: 'Description for a general offer page highlight tag about included premium perks.',
  },
  defaultSettingTitle: {
    id: 'hQtgzOA-_jdwz_utpdeS',
    defaultMessage: 'Beautiful resort setting',
    description: 'Title for a general offer page highlight tag about the resort setting.',
  },
  defaultSettingDescription: {
    id: '2Kd8RyU0FSvVFi_Fi7jG',
    defaultMessage: 'Pools, gardens and ocean views',
    description: 'Description for a general offer page highlight tag about the resort setting.',
  },
  greatForCouplesTitle: {
    id: 'OJTntylUVxe3Fc0p968A',
    defaultMessage: 'Great for couples',
    description: 'Title for an offer page highlight tag shown for couple-friendly stays.',
  },
  greatForCouplesDescription: {
    id: 'mRpuGAJvjpzPj_9P_JjJ',
    defaultMessage: 'Made for a romantic escape',
    description: 'Description for an offer page highlight tag shown for couple-friendly stays.',
  },
  familyFriendlyTitle: {
    id: '1P-rGCNE8z5fS4O2zwZ3',
    defaultMessage: 'Family friendly',
    description: 'Title for an offer page highlight tag shown for family-friendly stays.',
  },
  familyFriendlyDescription: {
    id: 'qUEShaMaOW-1VIx_8NQH',
    defaultMessage: 'Designed with families in mind',
    description: 'Description for an offer page highlight tag shown for family-friendly stays.',
  },
  allInclusiveTitle: {
    id: 'ApVNYp_FC6md-YbgNRHz',
    defaultMessage: 'All-inclusive',
    description: 'Title for an offer page highlight tag shown when meals and drinks are included.',
  },
  allInclusiveDescription: {
    id: '0_krx7UFojLlBFxjIS4x',
    defaultMessage: 'Food, drinks and more, all taken care of',
    description: 'Description for an offer page highlight tag shown when meals and drinks are included.',
  },
  breakfastIncludedTitle: {
    id: 'U6211to5p1uDppZatm7U',
    defaultMessage: 'Breakfast included',
    description: 'Title for an offer page highlight tag shown when breakfast is included.',
  },
  breakfastIncludedDescription: {
    id: 'cu0krTfkhyINL07RJeUD',
    defaultMessage: 'Start your day the right way',
    description: 'Description for an offer page highlight tag shown when breakfast is included.',
  },
  highlyRatedTitle: {
    id: 'IUBS6lNraEAJb74OSVng',
    defaultMessage: 'Highly rated by travellers',
    description: 'Title for an offer page highlight tag shown for highly rated properties.',
  },
  highlyRatedDescription: {
    id: '34rfjzNmtWV6llHzY-8z',
    defaultMessage: 'Trusted and loved by guests',
    description: 'Description for an offer page highlight tag shown for highly rated properties.',
  },
  skiInSkiOutTitle: {
    id: '92qta_u97n1tbBj4gTOp',
    defaultMessage: 'Ski-in/ski-out',
    description: 'Title for an offer page highlight tag shown for ski stays.',
  },
  skiInSkiOutDescription: {
    id: 'nu9IlNnrn3Z1WK3IVQWJ',
    defaultMessage: 'Hit the slopes right from your door',
    description: 'Description for an offer page highlight tag shown for ski stays.',
  },
  beachNearbyTitle: {
    id: 'V6ATLyzU-O4zgkkL6ce_',
    defaultMessage: 'Beach nearby',
    description: 'Title for an offer page highlight tag shown for properties near a beach.',
  },
  beachNearbyDescription: {
    id: 'iRnaNTT2DDj47R96X5lH',
    defaultMessage: 'Sand and sea within easy reach',
    description: 'Description for an offer page highlight tag shown for properties near a beach.',
  },
  centralLocationTitle: {
    id: 'o9Le6JPWRylp1nIcs-w5',
    defaultMessage: 'Central location',
    description: 'Title for an offer page highlight tag shown for centrally located properties.',
  },
  centralLocationDescription: {
    id: '3qbH4RLf7Mr1LUB_aWL6',
    defaultMessage: 'Right in the heart of it all',
    description: 'Description for an offer page highlight tag shown for centrally located properties.',
  },
  greatLocationTitle: {
    id: '52uaslsXRuWLnUUJfqcU',
    defaultMessage: 'Great location',
    description: 'Title for an offer page highlight tag shown for properties with a strong location.',
  },
  greatLocationDescription: {
    id: 'uDNzfJKB4Tbzxn1zYvQn',
    defaultMessage: 'Everything on your doorstep',
    description: 'Description for an offer page highlight tag shown for properties with a strong location.',
  },
  walkableNeighbourhoodTitle: {
    id: 'zTqZSSTfyiwXPrWyW-Eg',
    defaultMessage: 'Walkable neighbourhood',
    description: 'Title for an offer page highlight tag shown for walkable neighbourhoods.',
  },
  walkableNeighbourhoodDescription: {
    id: 'Xwarqjf0ZlFG2_fmkGTM',
    defaultMessage: 'Explore the area on foot',
    description: 'Description for an offer page highlight tag shown for walkable neighbourhoods.',
  },
  artsCultureNearbyTitle: {
    id: 'tGIdM59P3LovyOS8RDD-',
    defaultMessage: 'Arts & culture nearby',
    description: 'Title for an offer page highlight tag shown for nearby arts and culture.',
  },
  artsCultureNearbyDescription: {
    id: '_bOHLe5xQKKVx5Q5pmNN',
    defaultMessage: 'Museums, galleries and more on your doorstep',
    description: 'Description for an offer page highlight tag shown for nearby arts and culture.',
  },
  wineryAccessTitle: {
    id: 'xq_4VBPfcawMpKUbCYI7',
    defaultMessage: 'Winery & cellar door access',
    description: 'Title for an offer page highlight tag shown for nearby wineries.',
  },
  wineryAccessDescription: {
    id: 'jT-pjQCwemiEsuTfG0qD',
    defaultMessage: 'World-class wines right on your doorstep',
    description: 'Description for an offer page highlight tag shown for nearby wineries.',
  },
  golfNearbyTitle: {
    id: '06djRek7LlcvJzJPNS60',
    defaultMessage: 'Golf nearby',
    description: 'Title for an offer page highlight tag shown for nearby golf.',
  },
  golfNearbyDescription: {
    id: 'Vf0dg4JVs9FFLAguqzI3',
    defaultMessage: 'Tee off without going far',
    description: 'Description for an offer page highlight tag shown for nearby golf.',
  },
  easyAirportAccessTitle: {
    id: 'RpDT-y5t3gLgz9tH3L5R',
    defaultMessage: 'Easy airport access',
    description: 'Title for an offer page highlight tag shown for easy airport access.',
  },
  easyAirportAccessDescription: {
    id: 'ziHVv69SwJzR1hCLNTP9',
    defaultMessage: 'Arrive and depart with ease',
    description: 'Description for an offer page highlight tag shown for easy airport access.',
  },
  closeToTransportTitle: {
    id: 'EFLmszBZ4oYPxNwatW-Z',
    defaultMessage: 'Close to transport',
    description: 'Title for an offer page highlight tag shown for nearby transport.',
  },
  closeToTransportDescription: {
    id: 'ZNbfy4DLjZgp6-Lq3ZKd',
    defaultMessage: 'Easy to get around',
    description: 'Description for an offer page highlight tag shown for nearby transport.',
  },
  oceanViewRoomsTitle: {
    id: 'BWW7AZ3zuxDhPcpWsDYk',
    defaultMessage: 'Ocean-view rooms',
    description: 'Title for an offer page highlight tag shown for ocean-view rooms.',
  },
  oceanViewRoomsDescription: {
    id: 'RMSkKjx_KKfHJGIz7juo',
    defaultMessage: 'Wake up to water views',
    description: 'Description for an offer page highlight tag shown for ocean-view rooms.',
  },
  spaciousRoomsTitle: {
    id: '77_gny1hd5GYzRyKQWWA',
    defaultMessage: 'Spacious rooms',
    description: 'Title for an offer page highlight tag shown for spacious rooms.',
  },
  spaciousRoomsDescription: {
    id: '7ceUIQsKTa0AIe8BNeM4',
    defaultMessage: 'Extra space to relax',
    description: 'Description for an offer page highlight tag shown for spacious rooms.',
  },
  interconnectingRoomsTitle: {
    id: 'wnOkaqmDXlDwUrFgAUMS',
    defaultMessage: 'Interconnecting rooms',
    description: 'Title for an offer page highlight tag shown for interconnecting rooms.',
  },
  interconnectingRoomsDescription: {
    id: 'Z47TOp5ZbpwZJTyiM_KG',
    defaultMessage: 'Perfect for families or groups travelling together',
    description: 'Description for an offer page highlight tag shown for interconnecting rooms.',
  },
  adultsOnlyTitle: {
    id: 'Dh_MPe75w0mgySEM8mLa',
    defaultMessage: 'Adults only',
    description: 'Title for an offer page highlight tag shown for adults-only properties.',
  },
  adultsOnlyDescription: {
    id: 'rSEMpWKnT4tPNgsx71hA',
    defaultMessage: 'A retreat just for grown-ups',
    description: 'Description for an offer page highlight tag shown for adults-only properties.',
  },
  freeWifiTitle: {
    id: 'Mly28W2aTi3cMhhVqlqW',
    defaultMessage: 'Free Wi-Fi',
    description: 'Title for an offer page highlight tag shown when free Wi-Fi is available.',
  },
  freeWifiDescription: {
    id: 'ZsQVrexwSo_D3cmuhu9J',
    defaultMessage: 'Stay connected throughout your stay',
    description: 'Description for an offer page highlight tag shown when free Wi-Fi is available.',
  },
  receptionTitle: {
    id: 'hGV1TnOlP5YdpwNXBA6o',
    defaultMessage: '24-hour reception',
    description: 'Title for an offer page highlight tag shown for 24-hour reception.',
  },
  receptionDescription: {
    id: 'iFsurNDzoNobL17XYuxx',
    defaultMessage: 'Support any time, day or night',
    description: 'Description for an offer page highlight tag shown for 24-hour reception.',
  },
  expressCheckInTitle: {
    id: 'mLAFepNyLN0hjUgpeDf8',
    defaultMessage: 'Express check-in',
    description: 'Title for an offer page highlight tag shown for express check-in.',
  },
  expressCheckInDescription: {
    id: 'G0601bj0YHhKvmbXz2QJ',
    defaultMessage: 'Get settled faster on arrival',
    description: 'Description for an offer page highlight tag shown for express check-in.',
  },
  parkingTitle: {
    id: 'qCc9WR8e6ShTKUhJ56lg',
    defaultMessage: 'Parking',
    description: 'Title for an offer page highlight tag shown when parking is available.',
  },
  parkingDescription: {
    id: 'VjrkZWVEJ9IOsoaAmuNc',
    defaultMessage: 'Make the most of onsite parking',
    description: 'Description for an offer page highlight tag shown when parking is available.',
  },
  airportTransfersTitle: {
    id: 'bMzJonDlJukZZVdGABS7',
    defaultMessage: 'Airport transfers',
    description: 'Title for an offer page highlight tag shown when airport transfers are available.',
  },
  airportTransfersDescription: {
    id: 'agzLMhG-pQR-k8UqLXyO',
    defaultMessage: 'Straight from the airport to your door',
    description: 'Description for an offer page highlight tag shown when airport transfers are available.',
  },
  onsiteDiningTitle: {
    id: 'vh6nA7xiWVLnDCL5Trym',
    defaultMessage: 'Onsite dining',
    description: 'Title for an offer page highlight tag shown when onsite dining is available.',
  },
  onsiteDiningDescription: {
    id: 'LHbXO9G94hVBSmCInaL6',
    defaultMessage: 'Restaurant and dining onsite',
    description: 'Description for an offer page highlight tag shown when onsite dining is available.',
  },
  barLoungeTitle: {
    id: '78xNEp1lxLvo1KLxsppB',
    defaultMessage: 'Bar & lounge onsite',
    description: 'Title for an offer page highlight tag shown when a bar or lounge is available.',
  },
  barLoungeDescription: {
    id: 'pyGovaPyA7qpNxbYDftI',
    defaultMessage: 'Settle in and stay for a drink',
    description: 'Description for an offer page highlight tag shown when a bar or lounge is available.',
  },
  roomServiceTitle: {
    id: 'p97XcSYFzq_PS_7ukNn4',
    defaultMessage: 'Room service',
    description: 'Title for an offer page highlight tag shown when room service is available.',
  },
  roomServiceDescription: {
    id: 'ohY9P4VtSba6-o61UoC2',
    defaultMessage: 'Eat in the comfort of your room',
    description: 'Description for an offer page highlight tag shown when room service is available.',
  },
  outdoorPoolTitle: {
    id: 'pTMxl9QalOpgRaMYEdfn',
    defaultMessage: 'Outdoor pool',
    description: 'Title for an offer page highlight tag shown when an outdoor pool is available.',
  },
  outdoorPoolDescription: {
    id: 'FffIdaSXFlDqZfqdUu54',
    defaultMessage: 'Unwind poolside in the open air',
    description: 'Description for an offer page highlight tag shown when an outdoor pool is available.',
  },
  indoorPoolTitle: {
    id: '5xRspBR6WzwcXILaXPvu',
    defaultMessage: 'Indoor pool',
    description: 'Title for an offer page highlight tag shown when an indoor pool is available.',
  },
  indoorPoolDescription: {
    id: 'Y1qX2R1Run6grix-wAgp',
    defaultMessage: 'Swim year-round in comfort',
    description: 'Description for an offer page highlight tag shown when an indoor pool is available.',
  },
  kidsPoolTitle: {
    id: '_W3_kpBt48K-RCh86OvK',
    defaultMessage: 'Kids pool',
    description: 'Title for an offer page highlight tag shown when a kids pool is available.',
  },
  kidsPoolDescription: {
    id: 'tOTS8q8GqlPWcHnDrlP_',
    defaultMessage: 'Water fun for kids of all ages',
    description: 'Description for an offer page highlight tag shown when a kids pool is available.',
  },
  fitnessCentreTitle: {
    id: '3-6MO-N6DV558FUQMLwi',
    defaultMessage: 'Fitness centre',
    description: 'Title for an offer page highlight tag shown when a fitness centre is available.',
  },
  fitnessCentreDescription: {
    id: 'xyOIk23oZqGADCHc9qQQ',
    defaultMessage: 'Keep routines on track',
    description: 'Description for an offer page highlight tag shown when a fitness centre is available.',
  },
  spaWellnessTitle: {
    id: '4CDdKMf7fw9lr_q5shi4',
    defaultMessage: 'Spa & wellness facilities',
    description: 'Title for an offer page highlight tag shown when spa or wellness facilities are available.',
  },
  spaWellnessDescription: {
    id: 'ZH3ju9yRbHeTeUA1Wx1B',
    defaultMessage: 'Relax and recharge onsite',
    description: 'Description for an offer page highlight tag shown when spa or wellness facilities are available.',
  },
  accessibleFacilitiesTitle: {
    id: 'X-AvldWyU6EvvQGkNKxD',
    defaultMessage: 'Accessible facilities',
    description: 'Title for an offer page highlight tag shown when accessible facilities are available.',
  },
  accessibleFacilitiesDescription: {
    id: 'Wr-4tFrEySQqbWEmJzsh',
    defaultMessage: 'Built with accessibility in mind',
    description: 'Description for an offer page highlight tag shown when accessible facilities are available.',
  },
})

const FAMILY_TAG_CONFIGS: Array<OfferPageHighlightTagConfig> = [
  {
    id: 'family-pools',
    icon: 'water',
    titleMessage: tagMessages.familyPoolsTitle,
    descriptionMessage: tagMessages.familyPoolsDescription,
  },
  {
    id: 'family-location',
    icon: 'trees',
    titleMessage: tagMessages.familyLocationTitle,
    descriptionMessage: tagMessages.familyLocationDescription,
  },
  {
    id: 'family-dining',
    icon: 'restaurant',
    titleMessage: tagMessages.familyDiningTitle,
    descriptionMessage: tagMessages.familyDiningDescription,
  },
  {
    id: 'family-space',
    icon: 'bed-sofa',
    titleMessage: tagMessages.familySpaceTitle,
    descriptionMessage: tagMessages.familySpaceDescription,
  },
]

const COUPLE_TAG_CONFIGS: Array<OfferPageHighlightTagConfig> = [
  {
    id: 'couple-sunset',
    icon: 'sunset',
    titleMessage: tagMessages.coupleSunsetTitle,
    descriptionMessage: tagMessages.coupleSunsetDescription,
  },
  {
    id: 'couple-cocktails',
    icon: 'glass-martini',
    titleMessage: tagMessages.coupleCocktailsTitle,
    descriptionMessage: tagMessages.coupleCocktailsDescription,
  },
  {
    id: 'couple-resort',
    icon: 'trees',
    titleMessage: tagMessages.coupleResortTitle,
    descriptionMessage: tagMessages.coupleResortDescription,
  },
  {
    id: 'couple-service',
    icon: 'star',
    titleMessage: tagMessages.coupleServiceTitle,
    descriptionMessage: tagMessages.coupleServiceDescription,
  },
]

const DEFAULT_TAG_CONFIGS: Array<OfferPageHighlightTagConfig> = [
  {
    id: 'default-rating',
    icon: 'star',
    titleMessage: tagMessages.defaultRatingTitle,
    descriptionMessage: tagMessages.defaultRatingDescription,
  },
  {
    id: 'default-location',
    icon: 'map-marker',
    titleMessage: tagMessages.defaultLocationTitle,
    descriptionMessage: tagMessages.defaultLocationDescription,
  },
  {
    id: 'default-perks',
    icon: 'utensils',
    titleMessage: tagMessages.defaultPerksTitle,
    descriptionMessage: tagMessages.defaultPerksDescription,
  },
  {
    id: 'default-setting',
    icon: 'trees',
    titleMessage: tagMessages.defaultSettingTitle,
    descriptionMessage: tagMessages.defaultSettingDescription,
  },
]

const DATE_AWARE_DEFAULT_TAG_CONFIGS: Array<OfferPageHighlightTagConfig> = [
  DEFAULT_TAG_CONFIGS[0],
  {
    ...DEFAULT_TAG_CONFIGS[1],
    titleMessage: tagMessages.defaultLocationWithDatesTitle,
  },
  DEFAULT_TAG_CONFIGS[2],
  DEFAULT_TAG_CONFIGS[3],
]

const COUPLES_HOLIDAY_TYPE_SLUGS = new Set([
  'honeymoon',
  'romantic',
])

// CRO6684 mapping notes:
// - Great for couples: couple occupancy + Romantic/Honeymoon travel filter holiday type + not adults-only.
// - Family friendly: family occupancy + family/kid facility/holiday text + not adults-only.
// - All-inclusive: boardCode allinclusive, falling back to inclusion text.
// - Breakfast included: breakfast inclusion text, hidden when All-inclusive matches.
//   Rate-level boardCode breakfast is not shown.
// - Highly rated by travellers: review count >= 5 and rating >= 8.8 on the app's 10-point scale.
// - TODO P6 - Ski-in/ski-out: ski/snow holiday type.
//   TODO(CRO6684): use ski lift access flag or distance to nearest ski lift <= 500m.
// - TODO P7 - Beach nearby: currently not shown.
//   TODO(CRO6684): wire offer page nearby data and use distance to nearest beach <= 1km.
// - TODO P8 - Central location: currently not shown.
//   TODO(CRO6684): use distance to city centre <= 3km.
// - TODO P9 - Great location: currently not shown.
//   TODO(CRO6684): use POI counts within 1.5km for tourist attractions, restaurants and cafes.
// - TODO P10 - Walkable neighbourhood: currently not shown.
//   TODO(CRO6684): use POI counts within 500m for restaurants, cafes, shops and attractions.
// - TODO P11 - Arts & culture nearby: currently not shown.
//   TODO(CRO6684): use POI counts within 2km for galleries, museums, theatres and cultural centres.
// - TODO P12 - Winery & cellar door access: currently not shown.
//   TODO(CRO6684): use winery/vineyard POI counts within 10km.
// - TODO P13 - Golf nearby: currently not shown.
//   TODO(CRO6684): use golf course existence within 10km.
// - TODO P14 - Easy airport access: currently not shown.
//   TODO(CRO6684): use drive time to nearest major airport <= 45 minutes.
// - TODO P15 - Close to transport: currently not shown.
//   TODO(CRO6684): use transit station existence within 800m.
// - TODO P16 - Ocean-view rooms: room amenity/feature view text.
//   TODO(CRO6684): use structured room inventory view values.
// - TODO P17 - Spacious rooms: minimum available room size >= 35sqm.
//   Flash hotel has structured package room size; property pages parse room.size text;
//   bedbank uses room feature dimensions.
//   TODO(CRO6684): use normalised room size sqm across offer types and minimum
//   room-type sample when available.
// - TODO P18 - Interconnecting rooms: family/group occupancy + 3 or more guests + interconnecting room text.
//   TODO(CRO6684): use structured interconnecting-room availability.
// - TODO P19 - Adults only: adults-only flag or explicit adults-only text.
//   TODO(CRO6684): use structured adults-only policy data.
// - Free Wi-Fi: free/complimentary Wi-Fi text.
// - TODO P21 - 24-hour reception: 24-hour front desk/reception text.
//   TODO(CRO6684): use structured front desk hours.
// - TODO P22 - Express check-in: express check-in text.
//   TODO(CRO6684): use structured express check-in service flag.
// - TODO Parking: currently not shown.
//   TODO(CRO6684): use structured free onsite parking data. Inclusion text can
//   overclaim, e.g. "1 hour free parking" should not trigger a generic Parking tag.
// - TODO P24 - Airport transfers: currently not shown.
//   TODO(CRO6684): use structured transfer availability/inclusion flag.
// - TODO P25 - Onsite dining: currently not shown.
//   TODO(CRO6684): use structured onsite restaurant count.
// - TODO P26 - Bar & lounge onsite: currently not shown.
//   TODO(CRO6684): use structured onsite bar/lounge count.
// - Room service: room service text.
// - Outdoor pool: outdoor pool text.
// - Indoor pool: indoor pool text.
// - Kids pool: kids/children's pool text + not adults-only.
// - Fitness centre: gym/fitness text.
// - Spa & wellness facilities: spa/wellness/sauna text.
// - TODO P33 - Accessible facilities: currently not shown.
//   TODO(CRO6684): use structured accessibility feature count and agreed threshold.
const MAPPED_TAG_CONFIGS: Array<OfferPageHighlightMappingConfig> = [
  {
    id: 'great-for-couples',
    icon: 'glass-martini',
    titleMessage: tagMessages.greatForCouplesTitle,
    descriptionMessage: tagMessages.greatForCouplesDescription,
    isEligible: ({ hasRomanticHolidayType, isAdultsOnlyProperty, isCouple }) => isCouple && hasRomanticHolidayType && !isAdultsOnlyProperty,
  },
  {
    id: 'family-friendly',
    icon: 'family',
    titleMessage: tagMessages.familyFriendlyTitle,
    descriptionMessage: tagMessages.familyFriendlyDescription,
    isEligible: ({ hasChild, hasFamilyFriendlySignals, isAdultsOnlyProperty }) => hasChild && hasFamilyFriendlySignals && !isAdultsOnlyProperty,
  },
  {
    id: 'all-inclusive',
    icon: 'utensils',
    titleMessage: tagMessages.allInclusiveTitle,
    descriptionMessage: tagMessages.allInclusiveDescription,
    isEligible: ({ hasAllInclusive }) => hasAllInclusive,
  },
  {
    id: 'breakfast-included',
    icon: 'cloch',
    titleMessage: tagMessages.breakfastIncludedTitle,
    descriptionMessage: tagMessages.breakfastIncludedDescription,
    isEligible: ({ hasAllInclusive, hasBreakfastIncluded }) => !hasAllInclusive && hasBreakfastIncluded,
  },
  {
    id: 'highly-rated',
    icon: 'star',
    titleMessage: tagMessages.highlyRatedTitle,
    descriptionMessage: tagMessages.highlyRatedDescription,
    isEligible: ({ context }) => (context?.rating?.reviewsTotal ?? 0) >= 5 && (context?.rating?.score ?? 0) >= 8.8,
  },
  {
    id: 'ski-in-ski-out',
    icon: 'ski-lift',
    titleMessage: tagMessages.skiInSkiOutTitle,
    descriptionMessage: tagMessages.skiInSkiOutDescription,
    isEligible: () => false,
  },
  {
    id: 'beach-nearby',
    icon: 'water',
    titleMessage: tagMessages.beachNearbyTitle,
    descriptionMessage: tagMessages.beachNearbyDescription,
    isEligible: () => false,
  },
  {
    id: 'central-location',
    icon: 'map-marker',
    titleMessage: tagMessages.centralLocationTitle,
    descriptionMessage: tagMessages.centralLocationDescription,
    isEligible: () => false,
  },
  {
    id: 'great-location',
    icon: 'map-marker',
    titleMessage: tagMessages.greatLocationTitle,
    descriptionMessage: tagMessages.greatLocationDescription,
    isEligible: () => false,
  },
  {
    id: 'walkable-neighbourhood',
    icon: 'walking',
    titleMessage: tagMessages.walkableNeighbourhoodTitle,
    descriptionMessage: tagMessages.walkableNeighbourhoodDescription,
    isEligible: () => false,
  },
  {
    id: 'arts-culture-nearby',
    icon: 'theatre',
    titleMessage: tagMessages.artsCultureNearbyTitle,
    descriptionMessage: tagMessages.artsCultureNearbyDescription,
    isEligible: () => false,
  },
  {
    id: 'winery-access',
    icon: 'grapes',
    titleMessage: tagMessages.wineryAccessTitle,
    descriptionMessage: tagMessages.wineryAccessDescription,
    isEligible: () => false,
  },
  {
    id: 'golf-nearby',
    icon: 'golf',
    titleMessage: tagMessages.golfNearbyTitle,
    descriptionMessage: tagMessages.golfNearbyDescription,
    isEligible: () => false,
  },
  {
    id: 'easy-airport-access',
    icon: 'plane',
    titleMessage: tagMessages.easyAirportAccessTitle,
    descriptionMessage: tagMessages.easyAirportAccessDescription,
    isEligible: () => false,
  },
  {
    id: 'close-to-transport',
    icon: 'car',
    titleMessage: tagMessages.closeToTransportTitle,
    descriptionMessage: tagMessages.closeToTransportDescription,
    isEligible: () => false,
  },
  {
    id: 'ocean-view-rooms',
    icon: 'water',
    titleMessage: tagMessages.oceanViewRoomsTitle,
    descriptionMessage: tagMessages.oceanViewRoomsDescription,
    isEligible: ({ context }) => hasAnyText(context?.roomViewTags ?? [], ['ocean view', 'sea view', 'water view']),
  },
  {
    id: 'spacious-rooms',
    icon: 'bed-sofa',
    titleMessage: tagMessages.spaciousRoomsTitle,
    descriptionMessage: tagMessages.spaciousRoomsDescription,
    isEligible: ({ context }) => hasMinimumRoomSizeSqm(context, 35),
  },
  {
    id: 'interconnecting-rooms',
    icon: 'bed-sofa',
    titleMessage: tagMessages.interconnectingRoomsTitle,
    descriptionMessage: tagMessages.interconnectingRoomsDescription,
    isEligible: ({ hasTotalGuestCountForGroup, isGroupOrFamily, searchableText }) =>
      isGroupOrFamily && hasTotalGuestCountForGroup && hasAnyText(searchableText, ['interconnecting', 'connecting room', 'adjoining room']),
  },
  {
    id: 'adults-only',
    icon: 'sunset',
    titleMessage: tagMessages.adultsOnlyTitle,
    descriptionMessage: tagMessages.adultsOnlyDescription,
    isEligible: ({ isAdultsOnlyProperty }) => isAdultsOnlyProperty,
  },
  {
    id: 'free-wifi',
    icon: 'wifi',
    titleMessage: tagMessages.freeWifiTitle,
    descriptionMessage: tagMessages.freeWifiDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['free wifi', 'free wi-fi', 'complimentary wifi', 'complimentary wi-fi']),
  },
  {
    id: 'reception',
    icon: 'clock',
    titleMessage: tagMessages.receptionTitle,
    descriptionMessage: tagMessages.receptionDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['24-hour front desk', '24 hour front desk', '24/7 front desk', '24-hour reception']),
  },
  {
    id: 'express-check-in',
    icon: 'clock',
    titleMessage: tagMessages.expressCheckInTitle,
    descriptionMessage: tagMessages.expressCheckInDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['express check-in', 'express check in']),
  },
  {
    id: 'parking',
    icon: 'parking',
    titleMessage: tagMessages.parkingTitle,
    descriptionMessage: tagMessages.parkingDescription,
    // Hidden until structured free onsite parking data is available. Inclusion text
    // like "1 hour free parking" can incorrectly overclaim generic parking availability.
    // isEligible: ({ hasFreeParkingIncluded }) => hasFreeParkingIncluded,
    isEligible: () => false,
  },
  {
    id: 'airport-transfers',
    icon: 'plane',
    titleMessage: tagMessages.airportTransfersTitle,
    descriptionMessage: tagMessages.airportTransfersDescription,
    isEligible: () => false,
  },
  {
    id: 'onsite-dining',
    icon: 'restaurant',
    titleMessage: tagMessages.onsiteDiningTitle,
    descriptionMessage: tagMessages.onsiteDiningDescription,
    isEligible: () => false,
  },
  {
    id: 'bar-lounge',
    icon: 'glass-martini',
    titleMessage: tagMessages.barLoungeTitle,
    descriptionMessage: tagMessages.barLoungeDescription,
    isEligible: () => false,
  },
  {
    id: 'room-service',
    icon: 'cloch',
    titleMessage: tagMessages.roomServiceTitle,
    descriptionMessage: tagMessages.roomServiceDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['room service']),
  },
  {
    id: 'outdoor-pool',
    icon: 'swimmer',
    titleMessage: tagMessages.outdoorPoolTitle,
    descriptionMessage: tagMessages.outdoorPoolDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['outdoor pool']),
  },
  {
    id: 'indoor-pool',
    icon: 'swimmer',
    titleMessage: tagMessages.indoorPoolTitle,
    descriptionMessage: tagMessages.indoorPoolDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['indoor pool']),
  },
  {
    id: 'kids-pool',
    icon: 'kid',
    titleMessage: tagMessages.kidsPoolTitle,
    descriptionMessage: tagMessages.kidsPoolDescription,
    isEligible: ({ isAdultsOnlyProperty, searchableText }) => !isAdultsOnlyProperty && hasAnyText(searchableText, ['kids pool', 'children pool', 'children\'s pool']),
  },
  {
    id: 'fitness-centre',
    icon: 'dumbbell',
    titleMessage: tagMessages.fitnessCentreTitle,
    descriptionMessage: tagMessages.fitnessCentreDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['gym', 'fitness centre', 'fitness center', 'fitness facilities']),
  },
  {
    id: 'spa-wellness',
    icon: 'wellness',
    titleMessage: tagMessages.spaWellnessTitle,
    descriptionMessage: tagMessages.spaWellnessDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['spa', 'wellness centre', 'wellness center', 'sauna']),
  },
  {
    id: 'accessible-facilities',
    icon: 'wheelchair',
    titleMessage: tagMessages.accessibleFacilitiesTitle,
    descriptionMessage: tagMessages.accessibleFacilitiesDescription,
    isEligible: () => false,
  },
]

function hasMinimumRoomSizeSqm(context: OfferPageHighlightTagContext | undefined, minimumSizeSqm: number) {
  const roomSizesSqm = context?.roomSizesSqm
  if (!roomSizesSqm?.length) return false
  if (context?.roomSizeAvailableRoomCount !== roomSizesSqm.length) return false

  return Math.min(...roomSizesSqm) >= minimumSizeSqm
}

function normaliseHighlightText(value: string) {
  return value.toLowerCase()
}

function escapeHighlightTextPattern(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function hasHighlightTextMatch(text: string, needle: string) {
  const textPattern = normaliseHighlightText(text)
  const needlePattern = escapeHighlightTextPattern(normaliseHighlightText(needle))
  const matchPattern = new RegExp(`(^|[^a-z0-9])${needlePattern}([^a-z0-9]|$)`)

  return matchPattern.test(textPattern)
}

function hasAnyText(textItems: Array<string>, needles: Array<string>) {
  return textItems.some(text => needles.some(needle => hasHighlightTextMatch(text, needle)))
}

// Parking tag matching is disabled until structured free onsite parking data is
// available. Text inclusions can be limited offers, e.g. "1 hour free parking".
// const FREE_PARKING_INCLUDED_TERMS = [
//   'free',
//   'complimentary',
// ]
//
// const PARKING_INCLUDED_TERMS = [
//   'parking',
//   'self-parking',
//   'self parking',
//   'valet',
// ]
//
// const INCLUDED_PARKING_TERMS = [
//   'included parking',
//   'parking included',
//   'parking is included',
// ]
//
// const PAID_PARKING_TERMS = [
//   'additional cost',
//   'at cost',
//   'charge',
//   'excluded',
//   'fee',
//   'not included',
//   'paid',
//   'surcharge',
// ]

function getOfferPageHighlightSearchableText(context?: OfferPageHighlightTagContext) {
  return [
    ...(context?.facilities ?? []),
    ...(context?.holidayTypes ?? []),
    ...(context?.inclusions ?? []),
    ...(context?.locationTags ?? []),
    ...(context?.roomAmenities ?? []),
    ...(context?.roomNames ?? []),
    ...(context?.roomViewTags ?? []),
  ].filter(Boolean)
}

function hasBoardCode(context: OfferPageHighlightTagContext | undefined, boardCodes: Array<App.RateBoardCode>) {
  return context?.boardCodes?.some(boardCode => boardCodes.includes(boardCode)) ?? false
}

function hasAllInclusiveSignal(context: OfferPageHighlightTagContext | undefined, searchableText: Array<string>) {
  if (hasBoardCode(context, ['allinclusive'])) return true

  return hasAnyText(searchableText, ['all-inclusive', 'all inclusive']) || (
    hasAnyText(searchableText, ['all meals', 'full board']) &&
    hasAnyText(searchableText, ['unlimited drinks', 'drinks included'])
  )
}

function hasBreakfastIncludedSignal(context: OfferPageHighlightTagContext | undefined) {
  return hasAnyText(context?.inclusions ?? [], ['breakfast'])
}

// function hasFreeParkingIncludedSignal(context: OfferPageHighlightTagContext | undefined) {
//   return (context?.inclusions ?? []).some(inclusion => (
//     (
//       (
//         hasAnyText([inclusion], PARKING_INCLUDED_TERMS) &&
//         hasAnyText([inclusion], FREE_PARKING_INCLUDED_TERMS)
//       ) ||
//       hasAnyText([inclusion], INCLUDED_PARKING_TERMS)
//     ) &&
//     !hasAnyText([inclusion], PAID_PARKING_TERMS)
//   ))
// }

function hasRomanticHolidayTypeSignal(context: OfferPageHighlightTagContext | undefined) {
  return context?.holidayTypes?.some(holidayType => COUPLES_HOLIDAY_TYPE_SLUGS.has(slugify(holidayType))) ?? false
}

function getOfferPageHighlightMappingOptions(
  context: OfferPageHighlightTagContext | undefined,
  travellerContext: OfferPageHighlightTravellerContext,
  rooms: Array<App.Occupants> | undefined,
): OfferPageHighlightMappingOptions {
  const searchableText = getOfferPageHighlightSearchableText(context)
  const totalGuests = rooms?.reduce(
    (sum, room) => sum + room.adults + (room.children ?? 0) + (room.infants ?? 0) + (room.teenagers ?? 0),
    0,
  ) ?? 0
  const isAdultsOnlyProperty = !!context?.isAdultsOnly || hasAnyText(searchableText, ['adults only', 'adults-only', 'adult only'])

  return {
    context,
    hasAllInclusive: hasAllInclusiveSignal(context, searchableText),
    hasBreakfastIncluded: hasBreakfastIncludedSignal(context),
    hasChild: travellerContext === 'family',
    hasFamilyFriendlySignals: hasAnyText(searchableText, ['family', 'kids', 'children', 'babysitting', 'baby', 'child']),
    hasRomanticHolidayType: hasRomanticHolidayTypeSignal(context),
    hasTotalGuestCountForGroup: totalGuests >= 3,
    isAdultsOnlyProperty,
    isCouple: travellerContext === 'couple',
    isGroupOrFamily: travellerContext === 'family' || travellerContext === 'group',
    searchableText,
  }
}

function getOfferPageHighlightTitle(
  intl: IntlShape,
  {
    duration,
    firstName,
    hasSelectedDates,
    shouldUseFirstNameForNeutralContext,
    travellerContext,
  }: GetTitleOptions,
) {
  if (hasSelectedDates && duration) {
    if (firstName) {
      return intl.formatMessage(
        titleMessages.selectedDatesPersonalised,
        {
          firstName,
          duration,
        },
      )
    }

    return intl.formatMessage(
      titleMessages.selectedDates,
      {
        duration,
      },
    )
  }

  switch (travellerContext) {
    case 'family':
      if (firstName) {
        return intl.formatMessage(
          titleMessages.familyPersonalised,
          {
            firstName,
          },
        )
      }

      return intl.formatMessage(titleMessages.family)

    case 'couple':
      if (firstName) {
        return intl.formatMessage(
          titleMessages.couplePersonalised,
          {
            firstName,
          },
        )
      }

      return intl.formatMessage(titleMessages.couple)

    case 'group':
      return intl.formatMessage(titleMessages.group)

    case 'default':
      if (firstName && shouldUseFirstNameForNeutralContext) {
        return intl.formatMessage(
          titleMessages.neutralPersonalised,
          {
            firstName,
          },
        )
      }

      return intl.formatMessage(titleMessages.neutral)
  }
}

function getOfferPageHighlightTagConfigs(
  context: OfferPageHighlightTagContext | undefined,
  travellerContext: OfferPageHighlightTravellerContext,
  hasSelectedDates: boolean,
  rooms: Array<App.Occupants> | undefined,
) {
  if (context) {
    const mappingOptions = getOfferPageHighlightMappingOptions(context, travellerContext, rooms)
    const mappedTags = MAPPED_TAG_CONFIGS.filter(tagConfig => tagConfig.isEligible(mappingOptions)).slice(0, 4)

    if (mappedTags.length > 0) {
      return mappedTags
    }
  }

  if (hasSelectedDates) return DATE_AWARE_DEFAULT_TAG_CONFIGS

  switch (travellerContext) {
    case 'family':
      return FAMILY_TAG_CONFIGS

    case 'couple':
      return COUPLE_TAG_CONFIGS

    case 'group':
    case 'default':
      return DEFAULT_TAG_CONFIGS
  }
}

function formatOfferPageHighlightTags(
  intl: IntlShape,
  tagConfigs: Array<OfferPageHighlightTagConfig>,
): Array<OfferPageHighlightTag> {
  return tagConfigs.map(tagConfig => ({
    id: tagConfig.id,
    icon: tagConfig.icon,
    title: intl.formatMessage(tagConfig.titleMessage),
    description: intl.formatMessage(tagConfig.descriptionMessage),
  }))
}

function useOfferPageHighlightTags(props: Props) {
  const {
    context,
    duration,
    hasSelectedDates,
    isOccupancyContextKnown,
    propertyId,
    rooms,
  } = props
  const intl = useIntl()
  const account = useAppSelector(state => state.auth.account)
  const firstName = account.memberId ? account.givenName : undefined

  const travellerContext = getOfferPageHighlightTravellerContext(rooms, isOccupancyContextKnown)
  const [shouldUseFirstNameForNeutralContext, setShouldUseFirstNameForNeutralContext] = useState(false)

  useEffect(() => {
    if (!firstName || hasSelectedDates || travellerContext !== 'default') {
      setShouldUseFirstNameForNeutralContext(false)
      return
    }

    const viewedPropertyIds = getViewedOfferPageHighlightPropertyIds()
    const shouldUseFirstName = shouldUseFirstNameForNeutralOfferPageHighlight(viewedPropertyIds)
    const nextViewedPropertyIds = Array.from(new Set([...viewedPropertyIds, propertyId])).slice(-2)

    setViewedOfferPageHighlightPropertyIds(nextViewedPropertyIds)
    setShouldUseFirstNameForNeutralContext(shouldUseFirstName)
  }, [firstName, hasSelectedDates, propertyId, travellerContext])

  const title = useMemo(() => getOfferPageHighlightTitle(
    intl,
    {
      duration,
      firstName,
      hasSelectedDates,
      shouldUseFirstNameForNeutralContext,
      travellerContext,
    },
  ), [duration, firstName, hasSelectedDates, intl, shouldUseFirstNameForNeutralContext, travellerContext])

  const tags = useMemo<Array<OfferPageHighlightTag>>(() => formatOfferPageHighlightTags(
    intl,
    getOfferPageHighlightTagConfigs(context, travellerContext, hasSelectedDates, rooms),
  ), [context, hasSelectedDates, intl, rooms, travellerContext])

  return {
    title,
    tags,
  }
}

export default useOfferPageHighlightTags
